/**
 * Combined country + language picker. Chip + popover on desktop;
 * same chip as an accordion in the hamburger (panel starts collapsed).
 * Dawn MenuDrawer binds every <button> as a close control — trigger/Save
 * use capture stopImmediatePropagation so the menu stays open while choosing.
 * Ship to updates the language list from each country's market languages
 * so Arabic appears when UAE is chosen, before Save.
 */
if (!customElements.get('daddl-localization')) {
  customElements.define(
    'daddl-localization',
    class DaddlLocalization extends HTMLElement {
      constructor() {
        super();
        this.header = document.querySelector('.header-wrapper');
        this.form = this.querySelector('form');
        this.trigger = this.querySelector('[data-daddl-loc-trigger]');
        this.panel = this.querySelector('[data-daddl-loc-panel]');
        this.isDrawer = this.classList.contains('daddl-loc--drawer');

        this.onTriggerClick = this.onTriggerClick.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onSaveClick = this.onSaveClick.bind(this);

        if (this.trigger) {
          this.trigger.addEventListener('click', this.onTriggerClick, true);
        }

        this.saveButton = this.querySelector('.daddl-loc__save');
        if (this.saveButton) {
          this.saveButton.addEventListener('click', this.onSaveClick, true);
        }

        this.addEventListener('daddl-select:change', (event) => {
          const field = event.target.getAttribute('data-loc-field');
          if (field === 'country') this.syncCountry(event.detail.option);
          if (field === 'language') this.syncLanguage(event.detail.option);
        });

        this.addEventListener('keydown', this.onKeyDown);
      }

      connectedCallback() {
        document.addEventListener('click', this.onDocumentClick);
        this.syncLanguagesForCountry(this.selectedCountryOption());
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
      }

      get isOpen() {
        return this.panel && !this.panel.hasAttribute('hidden');
      }

      onSaveClick(event) {
        event.stopImmediatePropagation();
      }

      onTriggerClick(event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.isOpen ? this.closePanel() : this.openPanel();
      }

      openPanel() {
        if (!this.panel) return;
        this.panel.removeAttribute('hidden');
        if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
        if (this.hasAttribute('data-prevent-hide') && this.header) {
          this.header.preventHide = true;
        }
        if (this.isDrawer) {
          this.panel.scrollIntoView({ block: 'nearest' });
        }
      }

      closePanel() {
        if (!this.panel) return;
        this.panel.setAttribute('hidden', '');
        if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
        this.closeFields();
        if (this.hasAttribute('data-prevent-hide') && this.header) {
          this.header.preventHide = false;
        }
      }

      closeFields() {
        this.querySelectorAll('daddl-select').forEach((select) => {
          if (typeof select.close === 'function') select.close();
        });
      }

      selectedCountryOption() {
        const countrySelect = this.querySelector('daddl-select[data-loc-field="country"]');
        if (!countrySelect || typeof countrySelect.options !== 'function') return null;
        return (
          countrySelect.options().find((option) => option.getAttribute('aria-selected') === 'true') ||
          countrySelect.options()[0] ||
          null
        );
      }

      parseCountryLanguages(option) {
        if (!option) return [];
        const raw = option.getAttribute('data-languages');
        if (!raw) return [];
        try {
          const parsed = JSON.parse(raw);
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          return [];
        }
      }

      syncCountry(option) {
        if (!option) return;
        const iso = option.dataset.iso;
        const currency = option.dataset.currency;
        const currencyName = option.dataset.currencyName;

        this.syncFlag(this.querySelector('[data-daddl-loc-trigger-flag]'), iso);
        const triggerCurrency = this.querySelector('[data-daddl-loc-trigger-currency]');
        if (triggerCurrency) triggerCurrency.textContent = currency;

        const currencyDisplay = this.querySelector('[data-daddl-loc-currency-display]');
        if (currencyDisplay) {
          currencyDisplay.textContent = currencyName ? `${currency} (${currencyName})` : currency;
        }

        this.syncLanguagesForCountry(option);
      }

      // Swap the language list to the pending country's market languages (before Save).
      syncLanguagesForCountry(option) {
        const field = this.querySelector('[data-daddl-loc-field="language"]');
        const languageSelect = this.querySelector('daddl-select[data-loc-field="language"]');
        if (!field || !languageSelect || typeof languageSelect.replaceOptions !== 'function') return;

        const languages = this.parseCountryLanguages(option);
        if (languages.length < 2) {
          field.hidden = true;
          if (languages[0]) {
            languageSelect.replaceOptions(
              [
                {
                  value: languages[0].iso,
                  label: languages[0].endonym,
                  lang: languages[0].iso,
                  dataset: {
                    isoShort: languages[0].short,
                    name: languages[0].endonym,
                  },
                },
              ],
              languages[0].iso
            );
            this.syncLanguage(languageSelect.options()[0]);
          }
          return;
        }

        field.hidden = false;
        const currentIso = languageSelect.input?.value;
        languageSelect.replaceOptions(
          languages.map((language) => ({
            value: language.iso,
            label: language.endonym,
            lang: language.iso,
            dataset: {
              isoShort: language.short,
              name: language.endonym,
            },
          })),
          currentIso
        );
        const selected =
          languageSelect.options().find((opt) => opt.getAttribute('aria-selected') === 'true') ||
          languageSelect.options()[0];
        this.syncLanguage(selected);
      }

      syncLanguage(option) {
        if (!option) return;
        const triggerLang = this.querySelector('[data-daddl-loc-trigger-lang]');
        if (triggerLang) triggerLang.textContent = option.dataset.isoShort;
      }

      syncFlag(el, iso) {
        if (!el || !iso) return;
        el.className = `fi fi-${iso} localization-form__flag`;
        if (el.hasAttribute('data-daddl-loc-trigger-flag')) {
          el.classList.add('daddl-loc__flag');
        }
      }

      onDocumentClick(event) {
        if (this.isDrawer) return;
        if (!this.contains(event.target)) this.closePanel();
      }

      onKeyDown(event) {
        if (event.code.toUpperCase() !== 'ESCAPE') return;
        if (this.querySelector('daddl-select [aria-expanded="true"]')) return;
        if (!this.isOpen) return;
        event.preventDefault();
        this.closePanel();
        if (this.trigger) this.trigger.focus();
      }
    }
  );
}
